/**
 * Fase 38.5 — Sección "Findings" embebida en SessionDetail.
 *
 * Lista findings de la sesión + último run del worker. Sirve como punto de
 * entrada al inbox global (`/findings`) y al detail por finding.
 */
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  enqueueAnalyzeRun,
  fetchAnalyzeRuns,
  fetchFindings,
  type AnalyzeRunListItem,
  type FindingListItem,
} from '../../api/admin/findings.js';
import { nanoToUsdCompact } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER, isGatedModule, isGatedModuleError } from '../../lib/privacy.js';

function relTime(iso: string): string {
  const delta = Math.max(0, Date.now() - new Date(iso).getTime());
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  return new Date(iso).toLocaleString();
}

interface Props {
  sessionId: string;
  /**
   * `module_id` de la sesión (null en el general assistant). Necesario para
   * el disable PREVENTIVO del re-run en módulos sensibles: en una sesión
   * gateada sin runs ni findings históricos no viaja ningún flag `gated`
   * por item, y sin esto el botón quedaba habilitado contra un endpoint
   * que responde 403 `gated_module`.
   */
  moduleId: string | null;
}

export function SessionFindingsSection({ sessionId, moduleId }: Props): ReactElement {
  const queryClient = useQueryClient();
  const runsQ = useQuery({
    queryKey: ['admin', 'analyze-runs', sessionId],
    queryFn: () => fetchAnalyzeRuns(sessionId),
    refetchInterval: 15_000,
  });
  const findingsQ = useQuery({
    queryKey: ['admin', 'findings', 'by-session', sessionId],
    queryFn: () => fetchFindings({ sessionId }),
  });
  const rerun = useMutation({
    mutationFn: () => enqueueAnalyzeRun(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analyze-runs', sessionId] });
    },
  });

  const lastRun: AnalyzeRunListItem | undefined = runsQ.data?.items[0];
  // Gate de privacidad de módulos sensibles: el flag viaja por item en ambas
  // respuestas (el backend nulifica el contenido textual read-time). Con
  // contenido gateado el re-run también va disabled — el endpoint responde
  // 403 `gated_module` (patrón Findings/Detail). `isGatedModule(moduleId)`
  // cubre el hueco de sesiones gateadas SIN items (ningún flag viaja); los
  // flags por item quedan como cinturón para el kill-switch del espejo
  // (VITE_ADMIN_SENSITIVE_GATE=off con el gate del backend activo).
  const gated =
    isGatedModule(moduleId) ||
    lastRun?.gated === true ||
    (findingsQ.data?.items.some((it: FindingListItem) => it.gated === true) ?? false);

  return (
    <section className="sess-distill" style={{ padding: 'var(--s-4) var(--s-6)' }}>
      <div
        className="flex"
        style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}
      >
        <h3 style={{ margin: 0 }}>Offline analysis (Fase 38)</h3>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => rerun.mutate()}
          disabled={rerun.isPending || gated}
          title={
            gated
              ? 'Módulo sensible — el re-run está bloqueado por el gate de privacidad'
              : undefined
          }
        >
          {rerun.isPending ? 'Encolando…' : 're-run analyze'}
        </button>
      </div>

      {rerun.isError ? (
        <div className="t-meta" style={{ color: 'var(--danger)', marginBottom: 8 }}>
          re-run falló: {rerun.error.message}
        </div>
      ) : null}

      {lastRun ? (
        <div className="t-meta" style={{ marginBottom: 8 }}>
          Última run: <code className="mono">{lastRun.status}</code> ·{' '}
          iter {lastRun.iterations} ·{' '}
          {lastRun.findingsAccepted}/{lastRun.findingsProposed} accepted ·{' '}
          {nanoToUsdCompact(lastRun.totalProviderCostNano)} ·{' '}
          <span title={lastRun.startedAt}>{relTime(lastRun.startedAt)}</span>
          {lastRun.errorText ? (
            <>
              {' · '}
              <span style={{ color: 'var(--danger)' }}>{lastRun.errorText}</span>
            </>
          ) : null}
          {lastRun.gated ? (
            <>
              {' · '}
              <span className="t-meta">🔒 {GATED_CONTENT_PLACEHOLDER}</span>
            </>
          ) : lastRun.readerSummary ? (
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: 'pointer' }}>Veredicto del Reader</summary>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{lastRun.readerSummary}</p>
            </details>
          ) : null}
        </div>
      ) : (
        <div className="t-meta" style={{ marginBottom: 8 }}>Sin runs registrados aún.</div>
      )}

      {findingsQ.isPending ? (
        <div className="loading">Cargando findings…</div>
      ) : findingsQ.isError ? (
        // 403 gated_module (módulo sensible) NO es un error: placeholder
        // sobrio, igual que chat/replay/span-io. DEFENSIVO — hoy `GET
        // /admin/findings` nulifica (200 + `gated: true` por item) en vez de
        // 403-ear; el único 403 `gated_module` de findings es el POST del
        // re-run. Si el endpoint pasara a 403-ear, el placeholder ya está.
        isGatedModuleError(findingsQ.error) ? (
          <div className="loading" data-testid="session-findings-gated">
            🔒 {GATED_CONTENT_PLACEHOLDER}
          </div>
        ) : (
          <div className="loading">Error: {findingsQ.error.message}</div>
        )
      ) : findingsQ.data.items.length === 0 ? (
        <div className="empty">Sin findings para esta sesión.</div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {findingsQ.data.items.map((it: FindingListItem) => (
            <li
              key={it.id}
              style={{
                padding: '8px 0',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                gap: 12,
                alignItems: 'baseline',
              }}
            >
              <span className="badge">S{it.severity}</span>
              <span className="mono t-meta">{it.category ?? '—'}</span>
              <Link to={`/findings/${it.id}`} style={{ flexGrow: 1 }}>
                {it.gated ? <span className="t-meta">{GATED_CONTENT_PLACEHOLDER}</span> : it.title}
              </Link>
              {it.regressionOf && (
                <span
                  className="badge b-danger"
                  title="Matchea contra un fix shipped — el bug volvió"
                >
                  🚨 reg
                </span>
              )}
              <span className="mono t-meta">{it.status}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
